/**
 * Template Analytics and Performance Monitoring
 * Production-ready analytics tracking for template operations
 */

class TemplateAnalytics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.performanceEntries = [];
    this.userActions = [];
    this.isEnabled = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ANALYTICS_ENABLED === 'true';
  }

  generateSessionId() {
    return `template_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Track template-specific actions with comprehensive metadata
   */
  trackTemplateAction(action, templateId = null, metadata = {}) {
    if (!this.isEnabled) return;

    const event = {
      event: 'template_action',
      action,
      template_id: templateId,
      session_id: this.sessionId,
      timestamp: Date.now(),
      user_agent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio || 1
      },
      connection: this.getConnectionInfo(),
      performance: this.getPerformanceSnapshot(),
      ...metadata
    };

    this.userActions.push(event);
    this.sendEvent(event);
  }

  /**
   * Track performance metrics with detailed analysis
   */
  trackPerformance(operation, duration, metadata = {}) {
    if (!this.isEnabled) return;

    const performanceData = {
      operation,
      duration,
      timestamp: Date.now(),
      performance_category: this.categorizePerformance(operation, duration),
      memory_usage: this.getMemoryUsage(),
      ...metadata
    };

    this.performanceEntries.push(performanceData);

    // Log slow operations
    if (duration > this.getPerformanceThreshold(operation)) {
      console.warn(`Slow ${operation}: ${duration}ms`, performanceData);
    }

    this.trackTemplateAction('performance_metric', null, performanceData);
  }

  /**
   * Track errors with context and stack traces
   */
  trackError(error, context = {}) {
    if (!this.isEnabled) return;

    const errorData = {
      error_message: error.message,
      error_stack: error.stack,
      error_type: error.constructor.name,
      timestamp: Date.now(),
      url: window.location.href,
      user_agent: navigator.userAgent,
      context
    };

    this.trackTemplateAction('error', null, errorData);

    // Also send to external error tracking
    this.sendErrorToExternalService(errorData);
  }

  /**
   * Track user engagement and behavior patterns
   */
  trackEngagement(action, duration = null, metadata = {}) {
    if (!this.isEnabled) return;

    const engagementData = {
      action,
      duration,
      timestamp: Date.now(),
      session_duration: Date.now() - this.startTime,
      page_interactions: this.userActions.length,
      ...metadata
    };

    this.trackTemplateAction('engagement', null, engagementData);
  }

  /**
   * Track template loading performance with detailed metrics
   */
  trackTemplateLoad(templateId, loadTime, metadata = {}) {
    const loadData = {
      template_id: templateId,
      load_time: loadTime,
      load_category: loadTime < 1000 ? 'fast' : loadTime < 3000 ? 'normal' : 'slow',
      cache_status: metadata.fromCache ? 'hit' : 'miss',
      image_size: metadata.imageSize || null,
      network_type: this.getNetworkType(),
      ...metadata
    };

    this.trackPerformance('template_load', loadTime, loadData);
  }

  /**
   * Track search performance and user behavior
   */
  trackSearch(query, resultCount, responseTime, metadata = {}) {
    const searchData = {
      query: query.substring(0, 100), // Limit for privacy
      query_length: query.length,
      result_count: resultCount,
      response_time: responseTime,
      search_type: metadata.searchType || 'text',
      filters_applied: metadata.filters || [],
      ...metadata
    };

    this.trackPerformance('search', responseTime, searchData);
  }

  /**
   * Get performance thresholds for different operations
   */
  getPerformanceThreshold(operation) {
    const thresholds = {
      'template_load': 2000,
      'template_gallery_load': 3000,
      'search': 1000,
      'template_save': 5000,
      'image_upload': 10000,
      'default': 2000
    };
    return thresholds[operation] || thresholds.default;
  }

  /**
   * Categorize performance based on operation and duration
   */
  categorizePerformance(operation, duration) {
    const threshold = this.getPerformanceThreshold(operation);
    if (duration < threshold * 0.5) return 'excellent';
    if (duration < threshold) return 'good';
    if (duration < threshold * 2) return 'poor';
    return 'critical';
  }

  /**
   * Get current memory usage information
   */
  getMemoryUsage() {
    if (performance.memory) {
      return {
        used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024), // MB
        total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024), // MB
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024) // MB
      };
    }
    return null;
  }

  /**
   * Get network connection information
   */
  getConnectionInfo() {
    if (navigator.connection) {
      return {
        effective_type: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        save_data: navigator.connection.saveData
      };
    }
    return null;
  }

  /**
   * Get network type for analytics
   */
  getNetworkType() {
    if (navigator.connection) {
      return navigator.connection.effectiveType || 'unknown';
    }
    return 'unknown';
  }

  /**
   * Get performance snapshot for current state
   */
  getPerformanceSnapshot() {
    return {
      memory: this.getMemoryUsage(),
      timing: performance.timing ? {
        navigation_start: performance.timing.navigationStart,
        load_event_end: performance.timing.loadEventEnd,
        dom_content_loaded: performance.timing.domContentLoadedEventEnd
      } : null,
      navigation_type: performance.navigation ? performance.navigation.type : null
    };
  }

  /**
   * Send event to analytics service
   */
  sendEvent(event) {
    if (!this.isEnabled) return;

    try {
      // Send to Google Analytics if available
      if (window.gtag) {
        window.gtag('event', event.action, {
          event_category: 'template',
          event_label: event.template_id,
          value: event.duration || 1,
          custom_parameter_session_id: event.session_id,
          custom_parameter_viewport: JSON.stringify(event.viewport),
          custom_parameter_performance: JSON.stringify(event.performance)
        });
      }

      // Send to custom analytics endpoint
      if (process.env.REACT_APP_ANALYTICS_ENDPOINT) {
        fetch(process.env.REACT_APP_ANALYTICS_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(event)
        }).catch(error => {
          console.warn('Failed to send analytics event:', error);
        });
      }

      // Store in localStorage for offline analysis (limited)
      this.storeOfflineEvent(event);

    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  }

  /**
   * Store events offline for later analysis
   */
  storeOfflineEvent(event) {
    try {
      const storageKey = 'template_analytics_offline';
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // Keep only last 50 events to prevent storage bloat
      stored.push(event);
      if (stored.length > 50) {
        stored.splice(0, stored.length - 50);
      }
      
      localStorage.setItem(storageKey, JSON.stringify(stored));
    } catch (error) {
      // Storage full or other error - ignore
    }
  }

  /**
   * Send error to external service (Sentry, etc.)
   */
  sendErrorToExternalService(errorData) {
    // Integrate with Sentry if available
    if (window.Sentry) {
      window.Sentry.captureException(new Error(errorData.error_message), {
        extra: errorData
      });
    }

    // Send to custom error tracking endpoint
    if (process.env.REACT_APP_ERROR_TRACKING_ENDPOINT) {
      fetch(process.env.REACT_APP_ERROR_TRACKING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData)
      }).catch(() => {
        // Ignore tracking errors
      });
    }
  }

  /**
   * Get analytics summary for debugging
   */
  getAnalyticsSummary() {
    return {
      session_id: this.sessionId,
      session_duration: Date.now() - this.startTime,
      total_actions: this.userActions.length,
      performance_entries: this.performanceEntries.length,
      memory_usage: this.getMemoryUsage(),
      connection: this.getConnectionInfo()
    };
  }

  /**
   * Export analytics data for debugging
   */
  exportAnalyticsData() {
    const data = {
      session: {
        id: this.sessionId,
        start_time: this.startTime,
        duration: Date.now() - this.startTime
      },
      user_actions: this.userActions,
      performance_entries: this.performanceEntries,
      system_info: {
        user_agent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        memory: this.getMemoryUsage(),
        connection: this.getConnectionInfo()
      }
    };

    // Create downloadable file for debugging
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `template-analytics-${this.sessionId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

// Global analytics instance
export const templateAnalytics = new TemplateAnalytics();

// Performance monitoring decorators
export const withPerformanceTracking = (operation) => {
  return (target, propertyKey, descriptor) => {
    const originalMethod = descriptor.value;
    
    descriptor.value = async function(...args) {
      const startTime = performance.now();
      try {
        const result = await originalMethod.apply(this, args);
        const duration = performance.now() - startTime;
        templateAnalytics.trackPerformance(operation, duration, {
          method: propertyKey,
          args_count: args.length
        });
        return result;
      } catch (error) {
        const duration = performance.now() - startTime;
        templateAnalytics.trackError(error, {
          operation,
          method: propertyKey,
          duration
        });
        throw error;
      }
    };
    
    return descriptor;
  };
};

// React hook for analytics
export const useTemplateAnalytics = () => {
  return {
    trackAction: templateAnalytics.trackTemplateAction.bind(templateAnalytics),
    trackPerformance: templateAnalytics.trackPerformance.bind(templateAnalytics),
    trackError: templateAnalytics.trackError.bind(templateAnalytics),
    trackEngagement: templateAnalytics.trackEngagement.bind(templateAnalytics),
    trackTemplateLoad: templateAnalytics.trackTemplateLoad.bind(templateAnalytics),
    trackSearch: templateAnalytics.trackSearch.bind(templateAnalytics),
    getSummary: templateAnalytics.getAnalyticsSummary.bind(templateAnalytics),
    exportData: templateAnalytics.exportAnalyticsData.bind(templateAnalytics)
  };
};

export default templateAnalytics;